import { useEffect, useCallback, useMemo } from 'react';
import { createDemoAwareClient } from '@/integrations/supabase/client';
import { useDemoV2 } from './useDemoV2';

type DemoOrderItemPayload = {
  item_id: string;
  item_name: string;
  quantity: number;
  price: number;
  notes: string;
};

type DemoOrderPayload = {
  table_number?: string | null;
  customer_name: string;
  customer_phone: string;
  items: DemoOrderItemPayload[];
  total_amount: number;
  payment_method: string;
  notes: string;
};

type DemoOrderItemFromStorage = {
  item_id: string | null;
  item_name: string | null;
  quantity: number | null;
  price: number;
  notes: string | null;
};

type DemoOrderFromStorage = {
  id: string;
  order_code: string | null;
  status: string | null;
  table_number: string;
  customer_name: string;
  customer_phone: string;
  items: DemoOrderItemFromStorage[];
  total_amount: number;
  payment_method: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
};

type DemoOrderItemRecord = {
  item_id: string | null;
  name_snapshot: string | null;
  quantity: number | null;
  price_snapshot_cents: number;
  notes: string | null;
};

type DemoOrderRecord = {
  id: string;
  order_code: string | null;
  status: string | null;
  demo_tables: { table_number: string | null } | null;
  total_cents: number;
  payment_method: string | null;
  notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  demo_order_items: DemoOrderItemRecord[] | null;
};

const isBrowser = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Background sync service that keeps localStorage in sync with Supabase
// without changing the dashboard UI
export const useBackgroundSync = () => {
  const demoContext = useDemoV2();
  const isDemoActive = demoContext?.isDemo ?? false;

  const demoRestaurantId = useMemo(() => {
    if (!isDemoActive) {
      return null;
    }
    if (demoContext?.demoRestaurantId) {
      return demoContext.demoRestaurantId;
    }
    if (!isBrowser) {
      return null;
    }
    return window.localStorage.getItem('braise:demo_restaurant_id_v2');
  }, [demoContext?.demoRestaurantId, isDemoActive]);

  const demoToken = useMemo(() => {
    if (!isDemoActive) {
      return undefined;
    }
    const contextToken = demoContext?.demoToken ?? demoContext?.demoJWT ?? null;
    if (contextToken) {
      return contextToken;
    }
    if (!isBrowser) {
      return undefined;
    }
    const storedToken = window.localStorage.getItem('braise:demo_jwt_v2');
    return storedToken || undefined;
  }, [demoContext?.demoJWT, demoContext?.demoToken, isDemoActive]);

  const client = useMemo(() => createDemoAwareClient(demoToken), [demoToken]);

  if (isDemoActive) {
    console.log('🔄 Background sync: Demo restaurant ID:', demoRestaurantId);
  }

  // Sync orders from Supabase to localStorage
  const syncOrdersFromSupabase = useCallback(async () => {
    if (!demoRestaurantId) return;

    try {
      console.log('🔄 Background sync: Loading orders from Supabase...');
      
      const { data: ordersDataRaw, error } = await client
        .from('demo_orders')
        .select(`
          *,
          demo_order_items (
            id,
            item_id,
            quantity,
            price_snapshot_cents,
            name_snapshot,
            notes
          ),
          demo_tables (
            table_number
          )
        `)
        .eq('demo_restaurant_id', demoRestaurantId)
        .order('created_at', { ascending: false });

      if (error) {
        const status = error.status ?? 'unknown';
        const code = error.code ?? 'unknown';
        console.warn(`[useDemoV2] orders fetch failed (status ${status}, code ${code}) – using fallback`);
        const existing = localStorage.getItem('braise:demo_orders_v2') || localStorage.getItem('braise:demo_orders');
        if (existing) {
          console.debug('[useDemoV2] using fallback for orders (existing LS)');
          try {
            const parsed = JSON.parse(existing);
            window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: parsed } }));
          } catch {
            // If parsing fails, seed fallback
            const now = new Date().toISOString();
            const fallbackOrders = [
              { id: 'order_fallback_1', order_code: 'ORD-001', status: 'PENDING_PAYMENT', table_number: '1', customer_name: 'Demo Guest', customer_phone: '', items: [ { item_id: 'item_1', item_name: 'Margherita Pizza', quantity: 1, price: 12.99, notes: '' }, { item_id: 'item_2', item_name: 'Lemonade', quantity: 2, price: 3.5, notes: '' } ], total_amount: 12.99 + (2 * 3.5), payment_method: 'CARD_TEST', notes: '', created_at: now, updated_at: now },
              { id: 'order_fallback_2', order_code: 'ORD-002', status: 'IN_PROGRESS', table_number: '3', customer_name: 'Demo Guest', customer_phone: '', items: [ { item_id: 'item_3', item_name: 'Caesar Salad', quantity: 1, price: 9.99, notes: 'No croutons' } ], total_amount: 9.99, payment_method: 'CARD_TEST', notes: '', created_at: now, updated_at: now },
            ];
            localStorage.setItem('braise:demo_orders_v2', JSON.stringify(fallbackOrders));
            localStorage.setItem('braise:demo_orders', JSON.stringify(fallbackOrders));
            window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: fallbackOrders } }));
          }
        } else {
          console.debug('[useDemoV2] using fallback for orders (seeded)');
          const now = new Date().toISOString();
          const fallbackOrders = [
            { id: 'order_fallback_1', order_code: 'ORD-001', status: 'PENDING_PAYMENT', table_number: '1', customer_name: 'Demo Guest', customer_phone: '', items: [ { item_id: 'item_1', item_name: 'Margherita Pizza', quantity: 1, price: 12.99, notes: '' }, { item_id: 'item_2', item_name: 'Lemonade', quantity: 2, price: 3.5, notes: '' } ], total_amount: 12.99 + (2 * 3.5), payment_method: 'CARD_TEST', notes: '', created_at: now, updated_at: now },
            { id: 'order_fallback_2', order_code: 'ORD-002', status: 'IN_PROGRESS', table_number: '3', customer_name: 'Demo Guest', customer_phone: '', items: [ { item_id: 'item_3', item_name: 'Caesar Salad', quantity: 1, price: 9.99, notes: 'No croutons' } ], total_amount: 9.99, payment_method: 'CARD_TEST', notes: '', created_at: now, updated_at: now },
          ];
          localStorage.setItem('braise:demo_orders_v2', JSON.stringify(fallbackOrders));
          localStorage.setItem('braise:demo_orders', JSON.stringify(fallbackOrders));
          window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { detail: { orders: fallbackOrders } }));
        }
        return;
      }

      const ordersData = ordersDataRaw as DemoOrderRecord[] | null;

      if (ordersData) {
        console.debug('[useDemoV2] using DB mode for orders');
        // Convert Supabase format to localStorage format
        const formattedOrders = ordersData.map(order => ({
          id: order.id,
          order_code: order.order_code,
          status: order.status,
          table_number: order.demo_tables?.table_number || 'Counter',
          customer_name: 'Demo Customer',
          customer_phone: '',
          items: order.demo_order_items?.map(item => ({
            item_id: item.item_id,
            item_name: item.name_snapshot,
            quantity: item.quantity,
            price: item.price_snapshot_cents / 100,
            notes: item.notes
          })) || [],
          total_amount: order.total_cents / 100,
          payment_method: order.payment_method,
          notes: order.notes,
          created_at: order.created_at,
          updated_at: order.updated_at
        }));

        // Update localStorage (both v2 and legacy key for compatibility)
        localStorage.setItem('braise:demo_orders_v2', JSON.stringify(formattedOrders));
        localStorage.setItem('braise:demo_orders', JSON.stringify(formattedOrders));
        console.log('✅ Background sync: Updated localStorage with', formattedOrders.length, 'orders');
        
        // Dispatch custom event to notify components
        window.dispatchEvent(new CustomEvent('demoOrdersUpdated', { 
          detail: { orders: formattedOrders } 
        }));
      }
    } catch (error) {
      console.error('Background sync error:', error);
    }
  }, [client, demoRestaurantId]);

  // Sync menu items from Supabase to localStorage
  const syncMenuFromSupabase = useCallback(async () => {
    if (!demoRestaurantId) return;

    try {
      console.log('🔄 Background sync: Loading menu from Supabase...');
      
      const { data: menuData, error } = await client
        .from('demo_menu_items')
        .select(`
          *,
          demo_menu_categories (
            name
          )
        `)
        .eq('demo_restaurant_id', demoRestaurantId)
        .eq('is_active', true);

      if (error) {
        const status = error.status ?? 'unknown';
        const code = error.code ?? 'unknown';
        console.warn(`[useDemoV2] menu fetch failed (status ${status}, code ${code}) – using fallback`);
        const existing = localStorage.getItem('braise:demo_menu_items_v2') || localStorage.getItem('braise:demo_menu_items');
        if (existing) {
          console.debug('[useDemoV2] using fallback for menu (existing LS)');
          try {
            const parsed = JSON.parse(existing);
            window.dispatchEvent(new CustomEvent('demoMenuUpdated', { detail: { menuItems: parsed } }));
          } catch {
            const fallbackMenuItems = [
              { id: 'item_1', name: 'Margherita Pizza', description: 'Fresh mozzarella, basil, tomato sauce', price: 12.99, category: 'Main Courses', is_available: true, preparation_time: 12, rating: 4.6 },
              { id: 'item_2', name: 'Lemonade', description: 'Freshly squeezed lemons', price: 3.5, category: 'Beverages', is_available: true, preparation_time: 1, rating: 4.3 },
              { id: 'item_3', name: 'Caesar Salad', description: 'Romaine, parmesan, croutons', price: 9.99, category: 'Appetizers', is_available: true, preparation_time: 8, rating: 4.4 },
            ];
            localStorage.setItem('braise:demo_menu_items_v2', JSON.stringify(fallbackMenuItems));
            localStorage.setItem('braise:demo_menu_items', JSON.stringify(fallbackMenuItems));
            window.dispatchEvent(new CustomEvent('demoMenuUpdated', { detail: { menuItems: fallbackMenuItems } }));
          }
        } else {
          console.debug('[useDemoV2] using fallback for menu (seeded)');
          const fallbackMenuItems = [
            { id: 'item_1', name: 'Margherita Pizza', description: 'Fresh mozzarella, basil, tomato sauce', price: 12.99, category: 'Main Courses', is_available: true, preparation_time: 12, rating: 4.6 },
            { id: 'item_2', name: 'Lemonade', description: 'Freshly squeezed lemons', price: 3.5, category: 'Beverages', is_available: true, preparation_time: 1, rating: 4.3 },
            { id: 'item_3', name: 'Caesar Salad', description: 'Romaine, parmesan, croutons', price: 9.99, category: 'Appetizers', is_available: true, preparation_time: 8, rating: 4.4 },
          ];
          localStorage.setItem('braise:demo_menu_items_v2', JSON.stringify(fallbackMenuItems));
          localStorage.setItem('braise:demo_menu_items', JSON.stringify(fallbackMenuItems));
          window.dispatchEvent(new CustomEvent('demoMenuUpdated', { detail: { menuItems: fallbackMenuItems } }));
        }
        return;
      }

      if (menuData) {
        console.debug('[useDemoV2] using DB mode for menu');
        const formattedMenuItems = menuData.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          price: item.price_cents / 100,
          category: item.demo_menu_categories?.name || 'Uncategorized',
          is_available: item.is_available,
          preparation_time: item.prep_minutes || 15,
          rating: 4.5
        }));

        localStorage.setItem('braise:demo_menu_items_v2', JSON.stringify(formattedMenuItems));
        localStorage.setItem('braise:demo_menu_items', JSON.stringify(formattedMenuItems));
        console.log('✅ Background sync: Updated localStorage with', formattedMenuItems.length, 'menu items');
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('demoMenuUpdated', { 
          detail: { menuItems: formattedMenuItems } 
        }));
      }
    } catch (error) {
      console.error('Background sync menu error:', error);
    }
  }, [client, demoRestaurantId]);

  // Setup real-time subscriptions
  useEffect(() => {
    if (!isBrowser || !isDemoActive) {
      return;
    }

    if (!demoRestaurantId) {
      console.log('❌ Background sync: No demo restaurant ID, skipping setup');
      console.log('❌ Background sync: Available localStorage keys:', Object.keys(localStorage).filter(k => k.startsWith('braise:')));
      return;
    }

    console.log('🔄 Background sync: Setting up real-time subscriptions for restaurant:', demoRestaurantId);
    console.log('🔄 Background sync: localStorage demo_restaurant_id_v2:', localStorage.getItem('braise:demo_restaurant_id_v2'));

    // Subscribe to orders changes
    const ordersChannel = client
      .channel(`background_orders_${demoRestaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'demo_orders',
          filter: `demo_restaurant_id=eq.${demoRestaurantId}`
        },
        (payload) => {
          console.log('🔄 Background sync: Order change detected:', payload.eventType);
          syncOrdersFromSupabase();
        }
      )
      .subscribe();

    // Subscribe to menu changes
    const menuChannel = client
      .channel(`background_menu_${demoRestaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'demo_menu_items',
          filter: `demo_restaurant_id=eq.${demoRestaurantId}`
        },
        (payload) => {
          console.log('🔄 Background sync: Menu change detected:', payload.eventType);
          syncMenuFromSupabase();
        }
      )
      .subscribe();

    // Initial sync
    syncOrdersFromSupabase();
    syncMenuFromSupabase();

    return () => {
      client.removeChannel(ordersChannel);
      client.removeChannel(menuChannel);
    };
  }, [client, demoRestaurantId, isDemoActive, syncOrdersFromSupabase, syncMenuFromSupabase]);

  // Function to place order to Supabase (called from customer menu)
  const placeOrderToSupabase = useCallback(async (orderData: DemoOrderPayload) => {
    console.log('📝 Background sync: placeOrderToSupabase called with:', orderData);
    console.log('📝 Background sync: Demo restaurant ID:', demoRestaurantId);
    
    if (!demoRestaurantId) {
      console.error('❌ No demo restaurant ID available - cannot place order');
      return;
    }

    try {
      console.log('📝 Background sync: Placing order to Supabase:', orderData);

      // Get demo location ID
      const { data: locationData } = await client
        .from('demo_locations')
        .select('id')
        .eq('demo_restaurant_id', demoRestaurantId)
        .limit(1)
        .single();

      if (!locationData) {
        console.error('No demo location found');
        return;
      }

      // Get demo table ID (if table number provided)
      let tableId = null;
      if (orderData.table_number && orderData.table_number !== 'Counter') {
        const { data: tableData } = await client
          .from('demo_tables')
          .select('id')
          .eq('demo_restaurant_id', demoRestaurantId)
          .eq('table_number', orderData.table_number)
          .limit(1)
          .single();
        
        tableId = tableData?.id || null;
      }

      // Generate order code
      const orderCode = `DEMO-${Date.now()}`;
      const trackingCode = `TRK-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

      // Calculate totals
      const subtotalCents = Math.round(orderData.total_amount * 100);
      const taxCents = Math.round(subtotalCents * 0.085); // 8.5% tax
      const totalCents = subtotalCents + taxCents;

      // Insert order
      const { data: orderResult, error: orderError } = await client
        .from('demo_orders')
        .insert({
          demo_restaurant_id: demoRestaurantId,
          table_id: tableId,
          location_id: locationData.id,
          order_code: orderCode,
          status: 'PENDING_PAYMENT',
          guest_count: 1,
          subtotal_cents: subtotalCents,
          tax_cents: taxCents,
          total_cents: totalCents,
          notes: orderData.notes,
          payment_method: orderData.payment_method === 'cash' ? 'CASH_MOCK' : 'CARD_TEST',
          payment_status: 'PAID',
          public_tracking_code: trackingCode,
          source: 'QR'
        })
        .select()
        .single();

      if (orderError) {
        console.error('Error creating order:', orderError);
        return;
      }

      // Insert order items
      const orderItems = orderData.items.map((item) => ({
        order_id: orderResult.id,
        item_id: item.item_id,
        quantity: item.quantity,
        price_snapshot_cents: Math.round(item.price * 100),
        name_snapshot: item.item_name,
        notes: item.notes
      }));

      const { error: itemsError } = await client
        .from('demo_order_items')
        .insert(orderItems);

      if (itemsError) {
        console.error('Error creating order items:', itemsError);
        return;
      }

      console.log('✅ Background sync: Order placed successfully:', orderResult);
      
      // Force sync orders after placing
      console.log('🔄 Background sync: Forcing order sync after placement...');
      await syncOrdersFromSupabase();
      
    } catch (error) {
      console.error('Background sync: Error placing order:', error);
    }
  }, [client, demoRestaurantId, syncOrdersFromSupabase]);

  // Function to update order status (called from kitchen page)
  const updateOrderStatusInSupabase = useCallback(async (orderId: string, newStatus: string) => {
    if (!demoRestaurantId) return;

    try {
      console.log('🔄 Background sync: Updating order status:', orderId, 'to', newStatus);

      const { error } = await client
        .from('demo_orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId)
        .eq('demo_restaurant_id', demoRestaurantId);

      if (error) {
        console.error('Error updating order status:', error);
        return;
      }

      console.log('✅ Background sync: Order status updated successfully');
      
      // Force sync orders after status update
      await syncOrdersFromSupabase();
      
    } catch (error) {
      console.error('Background sync: Error updating order status:', error);
    }
  }, [client, demoRestaurantId, syncOrdersFromSupabase]);

  // Function to update menu item availability (called from menu page)
  const updateMenuItemAvailabilityInSupabase = useCallback(async (itemId: string, isAvailable: boolean) => {
    if (!demoRestaurantId) return;

    try {
      console.log('🔄 Background sync: Updating menu item availability:', itemId, 'to', isAvailable);

      const { error } = await client
        .from('demo_menu_items')
        .update({ 
          is_available: isAvailable,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId)
        .eq('demo_restaurant_id', demoRestaurantId);

      if (error) {
        console.error('Error updating menu item availability:', error);
        return;
      }

      console.log('✅ Background sync: Menu item availability updated successfully');
      
      // Force sync menu after availability update
      await syncMenuFromSupabase();
      
    } catch (error) {
      console.error('Background sync: Error updating menu item availability:', error);
    }
  }, [client, demoRestaurantId, syncMenuFromSupabase]);

  // Function to calculate and update revenue stats
  const calculateRevenueStats = useCallback(() => {
    if (!isBrowser) {
      return { totalRevenue: 0, todayRevenue: 0, orderCount: 0 };
    }

    try {
      const storedOrders = localStorage.getItem('braise:demo_orders');
      if (!storedOrders) return { totalRevenue: 0, todayRevenue: 0, orderCount: 0 };

      const orders = JSON.parse(storedOrders) as DemoOrderFromStorage[];
      const today = new Date().toDateString();
      
      // Calculate total revenue from all orders
      const totalRevenue = orders.reduce((sum: number, order) => {
        return sum + (order.total_amount || 0);
      }, 0);

      // Calculate today's revenue
      const todayRevenue = orders.reduce((sum: number, order) => {
        const orderDate = new Date(order.created_at).toDateString();
        return orderDate === today ? sum + (order.total_amount || 0) : sum;
      }, 0);

      // Calculate order counts
      const orderCount = orders.length;
      const todayOrderCount = orders.filter((order) => {
        const orderDate = new Date(order.created_at).toDateString();
        return orderDate === today;
      }).length;

      const stats = {
        totalRevenue: Math.round(totalRevenue * 100) / 100,
        todayRevenue: Math.round(todayRevenue * 100) / 100,
        orderCount,
        todayOrderCount
      };

      // Store stats in localStorage
      localStorage.setItem('braise:demo_revenue_stats', JSON.stringify(stats));
      
      // Dispatch custom event for revenue updates
      window.dispatchEvent(new CustomEvent('demoRevenueUpdated', { 
        detail: { stats } 
      }));

      console.log('💰 Background sync: Revenue stats calculated:', stats);
      return stats;
    } catch (error) {
      console.error('Error calculating revenue stats:', error);
      return { totalRevenue: 0, todayRevenue: 0, orderCount: 0 };
    }
  }, []);

  // Enhanced sync function that also calculates revenue
  const syncOrdersAndRevenue = useCallback(async () => {
    await syncOrdersFromSupabase();
    calculateRevenueStats();
  }, [syncOrdersFromSupabase, calculateRevenueStats]);

  if (!isDemoActive) {
    return {
      syncOrdersFromSupabase: async () => {},
      syncMenuFromSupabase: async () => {},
      placeOrderToSupabase: async () => {},
      updateOrderStatusInSupabase: async () => {},
      updateMenuItemAvailabilityInSupabase: async () => {},
      calculateRevenueStats: () => ({ totalRevenue: 0, todayRevenue: 0, orderCount: 0 }),
      syncOrdersAndRevenue: async () => {},
    } as const;
  }

  return {
    syncOrdersFromSupabase,
    syncMenuFromSupabase,
    placeOrderToSupabase,
    updateOrderStatusInSupabase,
    updateMenuItemAvailabilityInSupabase,
    calculateRevenueStats,
    syncOrdersAndRevenue
  };
};
